import dayjs from 'dayjs'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
    ContentSearchParams,
    DatasetPrepTaskStatusView,
    useGetDatasetPrepTaskStatusLazyQuery,
    useStartPrepareChatSessionMutation
} from '../../../api/client'
import SearchParameters, {
    mapToContentSearchParams
} from '../../../components/content/search/searchParameters'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'

type DatasetReviewStepProps = {
    searchParameters: SearchParameters
    accountId: number
    selectedModel: string
    onBack: () => void
    onStartChat: (
        sessionId: string,
        itemCount: number,
        earliest: string | null,
        latest: string | null
    ) => void
}

const POLL_INTERVAL_MS = 1000

export default function DatasetReviewStep({
    searchParameters,
    accountId,
    selectedModel,
    onBack,
    onStartChat
}: DatasetReviewStepProps) {
    // State for task tracking
    const [progressMessage, setProgressMessage] = useState<string>(
        'Starting dataset preparation...'
    )
    const [isPolling, setIsPolling] = useState(false)

    // State for final result
    const [sessionId, setSessionId] = useState<string | null>(null)
    const [itemCount, setItemCount] = useState<number>(0)
    const [earliest, setEarliest] = useState<string | null>(null)
    const [latest, setLatest] = useState<string | null>(null)
    const [dateRangeReduced, setDateRangeReduced] = useState(false)
    const [prepareError, setPrepareError] = useState<string | null>(null)

    // Ref to track if component is mounted
    const isMounted = useRef(true)
    const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    // Mutation to start the preparation
    const [startPrepare, { loading: startLoading, error: startError }] =
        useStartPrepareChatSessionMutation()

    // Query to poll for status
    const [getTaskStatus] = useGetDatasetPrepTaskStatusLazyQuery({
        fetchPolicy: 'no-cache'
    })

    // Polling function
    const pollTaskStatus = useCallback(
        async (currentTaskId: string) => {
            if (!isMounted.current || !currentTaskId) return

            try {
                const { data, error } = await getTaskStatus({
                    variables: { taskId: currentTaskId }
                })

                if (error) {
                    setPrepareError(error.message)
                    setIsPolling(false)
                    return
                }

                const taskStatus = data?.getDatasetPrepTaskStatus
                if (!taskStatus) {
                    setPrepareError('No status data returned from server.')
                    setIsPolling(false)
                    return
                }

                // Update progress message
                if (taskStatus.progressMessage) {
                    setProgressMessage(taskStatus.progressMessage)
                }

                // Handle completion states
                if (taskStatus.status === DatasetPrepTaskStatusView.Success && taskStatus.result) {
                    const result = taskStatus.result
                    setSessionId(result.sessionId)
                    setItemCount(result.itemCount)
                    setEarliest(result.earliest)
                    setLatest(result.latest)
                    setDateRangeReduced(result.dateRangeWasReduced)
                    setIsPolling(false)
                    return
                }

                if (taskStatus.status === DatasetPrepTaskStatusView.Failed) {
                    setPrepareError(taskStatus.errorMessage || 'Dataset preparation failed.')
                    setIsPolling(false)
                    return
                }

                // Continue polling if still in progress
                if (isMounted.current) {
                    pollingTimeoutRef.current = setTimeout(() => {
                        pollTaskStatus(currentTaskId)
                    }, POLL_INTERVAL_MS)
                }
            } catch (err) {
                if (isMounted.current) {
                    setPrepareError(
                        err instanceof Error ? err.message : 'Failed to poll task status.'
                    )
                    setIsPolling(false)
                }
            }
        },
        [getTaskStatus]
    )

    // Start preparation on mount
    useEffect(() => {
        if (!accountId) return

        isMounted.current = true

        const startPreparation = async () => {
            const mappedParams = mapToContentSearchParams(searchParameters)
            const payload: ContentSearchParams = {
                ...mappedParams,
                maxResults: 1000,
                skipResults: 0
            }

            setPrepareError(null)
            setProgressMessage('Starting dataset preparation...')

            try {
                const { data, errors } = await startPrepare({
                    variables: {
                        accountId,
                        model: selectedModel,
                        searchParameters: payload
                    }
                })

                if (errors && errors.length > 0) {
                    setPrepareError(errors[0].message)
                    return
                }

                const newTaskId = data?.startPrepareChatSession?.taskId
                if (!newTaskId) {
                    setPrepareError('No task ID returned from server.')
                    return
                }

                setIsPolling(true)

                // Start polling
                pollTaskStatus(newTaskId)
            } catch (err) {
                setPrepareError(
                    err instanceof Error ? err.message : 'Failed to start dataset preparation.'
                )
            }
        }

        startPreparation()

        // Cleanup on unmount
        return () => {
            isMounted.current = false
            if (pollingTimeoutRef.current) {
                clearTimeout(pollingTimeoutRef.current)
            }
        }
    }, [accountId, selectedModel, searchParameters, startPrepare, pollTaskStatus])

    const formatDate = (value?: string | null) => {
        if (!value) return 'n/a'
        const parsed = dayjs(value)
        return parsed.isValid() ? parsed.format('MMM D, YYYY') : value
    }

    const handleStartChat = () => {
        if (sessionId) {
            onStartChat(sessionId, itemCount, earliest, latest)
        }
    }

    const isLoading = startLoading || isPolling
    const hasError = !!startError || !!prepareError
    const isStartDisabled = isLoading || !sessionId || itemCount === 0

    return (
        <div className="mx-auto max-w-3xl">
            <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-200 p-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Review Your Dataset</h2>
                <p className="text-gray-600 mb-8">
                    Review the dataset that Ask Arwen will analyze. Click Back to adjust your search
                    parameters, or Start Chat to begin.
                </p>

                {isLoading && (
                    <div className="flex flex-col items-center justify-center py-16">
                        <span className="inline-flex h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
                        <span className="mt-4 text-lg text-gray-600">{progressMessage}</span>
                    </div>
                )}

                {hasError && (
                    <div className="rounded-lg bg-red-50 p-6 text-red-700">
                        <h3 className="font-semibold mb-2">Error preparing dataset</h3>
                        <p>
                            {prepareError || startError?.message || 'An unexpected error occurred.'}
                        </p>
                    </div>
                )}

                {!isLoading && !hasError && sessionId && (
                    <div className="space-y-8">
                        {/* Item count display */}
                        <div className="text-center py-8 bg-gray-50 rounded-lg">
                            <div className="text-5xl font-bold text-primary-600">
                                {itemCount.toLocaleString()}
                            </div>
                            <div className="mt-2 text-lg text-gray-600">
                                {itemCount === 1 ? 'item' : 'items'} found
                            </div>
                        </div>

                        {/* Date range */}
                        <div className="flex items-center justify-center gap-4 text-gray-600">
                            <span className="font-medium">Date range:</span>
                            <span>
                                {formatDate(earliest)} — {formatDate(latest)}
                            </span>
                        </div>

                        {/* Warning if date range was reduced */}
                        {dateRangeReduced && (
                            <div className="flex items-start gap-3 rounded-lg bg-amber-50 p-4 text-amber-800">
                                <ExclamationTriangleIcon className="h-6 w-6 flex-shrink-0 text-amber-500" />
                                <div>
                                    <h4 className="font-semibold">Date range adjusted</h4>
                                    <p className="text-sm">
                                        The date range has been automatically adjusted to fit within
                                        model data limits. The dataset contains the most recent
                                        content from your selected range.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Zero items warning */}
                        {itemCount === 0 && (
                            <div className="flex items-start gap-3 rounded-lg bg-amber-50 p-4 text-amber-800">
                                <ExclamationTriangleIcon className="h-6 w-6 flex-shrink-0 text-amber-500" />
                                <div>
                                    <h4 className="font-semibold">No items found</h4>
                                    <p className="text-sm">
                                        No content matches your current filters. Please go back and
                                        adjust your search parameters to find content.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Navigation */}
                <div className="mt-10 flex justify-between">
                    <button
                        type="button"
                        onClick={onBack}
                        className="inline-flex items-center rounded-md border border-gray-300 bg-white px-6 py-3 text-base font-semibold text-gray-700 shadow-sm hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-500"
                    >
                        <svg
                            className="mr-2 h-5 w-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 19l-7-7 7-7"
                            />
                        </svg>
                        Back
                    </button>

                    <button
                        type="button"
                        onClick={handleStartChat}
                        disabled={isStartDisabled}
                        className="inline-flex items-center rounded-md bg-primary-600 px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
                    >
                        Start Chat
                        <svg
                            className="ml-2 h-5 w-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                            />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    )
}
